import { EstimateCurrency, EstimateFormat } from '../services/estimate-generator.service';

/** Estados persistidos en el API. */
export type PriceTemplateLifecycle = 'vigente' | 'convertida';

/** Estado de cara al usuario: el API lo resuelve y lo manda en `resolvedLifecycle`. */
export type PriceTemplateResolvedLifecycle = PriceTemplateLifecycle | 'vencida';

/** Variación seleccionada de un ítem, con el mismo shape que usa el carrito. */
export interface PriceTemplateItemVariation {
    itemId?: string;
    variation?: string;
}

/**
 * Ítem cotizado. `sellingPrice` es el precio congelado al momento de cotizar: no se
 * recalcula contra el inventario al abrir la cotización ni al convertirla en venta.
 */
export interface PriceTemplateItem {
    productId: string;
    productName: string;
    SKU?: string;
    amount: number;
    sellingPrice: number;
    iva?: number;
    variations?: PriceTemplateItemVariation[];
    location?: string;
    units?: string;
    referenceType?: string;
    /** Fracción, no porcentaje: 0.05 = 5%. 0 en las cotizaciones creadas desde el carrito. */
    discountPct?: number;
}

/** Snapshot del cliente al momento de cotizar. */
export interface PriceTemplateCustomer {
    customerId?: string;
    name?: string;
    dni?: string;
    dniType?: string;
    dniNumber?: string;
    phone?: string;
    email?: string;
    address?: string;
}

export interface PriceTemplate {
    _id?: string;
    name?: string;
    status?: string;
    lifecycle?: PriceTemplateLifecycle;
    resolvedLifecycle?: PriceTemplateResolvedLifecycle;
    /** `null` = sin vencimiento (FINA-3272). */
    expiresAt?: string | null;
    customer?: PriceTemplateCustomer;
    items?: PriceTemplateItem[];
    /** Solo viene en el listado: el detalle trae `items` completos. */
    itemsCount?: number;
    /** IVA calculado por el listado (`totalPrice` se guarda sin IVA). Solo lo trae `GET /price-templates`. */
    ivaTotal?: number;
    totalPrice?: number;
    currency?: EstimateCurrency;
    exchangeRate?: number;
    format?: EstimateFormat;
    observations?: string;
    /** Notas de cara al cliente, editables desde el módulo. */
    notes?: string;
    createdBy?: PriceTemplateCreatedBy;
    convertedBillId?: string;
    /** FINA-3273: `Bill.order` del pedido resultante. */
    convertedBillOrder?: number | null;
    convertedAt?: string;
    createdAt?: string;
    updatedAt?: string;
}

/** Un problema encontrado al validar los ítems contra el inventario. */
export interface PriceTemplateItemIssue {
    productId: string;
    productName: string;
    reason: string;
}

/**
 * Reporte de validación. `blocking` impide convertir o editar; `warnings` se muestran
 * pero dejan continuar (una cotización no compromete stock).
 */
export interface PriceTemplateValidationReport {
    blocking: PriceTemplateItemIssue[];
    warnings: PriceTemplateItemIssue[];
    canConvert: boolean;
    /** Detalle línea por línea para la tabla del modal de conversión. */
    lines: PriceTemplateStockLine[];
}

/** Filtros del listado del módulo. */
export interface PriceTemplateFilters {
    /** Rango sobre la fecha de creación, en yyyy-MM-dd. */
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    lifecycle?: PriceTemplateResolvedLifecycle | '';
    customerId?: string;
    /** Vendedor que creó la cotización (`createdBy.userId`). */
    userId?: string;
}

/** Snapshot de quién creó la cotización, para la columna "Creada por". */
export interface PriceTemplateCreatedBy {
    userId?: string;
    name?: string;
}

/** Una línea de la tabla de stock del modal de conversión. */
export interface PriceTemplateStockLine {
    productId: string;
    productName: string;
    SKU: string;
    /** Cantidad cotizada. */
    amount: number;
    /** Disponible hoy. `null` = no determinable o servicio; se muestra "—", no 0. */
    stock: number | null;
    /** Precio congelado, el que se respeta al convertir. */
    quotedPrice: number;
    currentPrice: number | null;
    priceChanged: boolean;
    isService: boolean;
    blocked: boolean;
    hasStock: boolean;
}

/** KPIs de la banda del listado. */
export interface PriceTemplateStats {
    total: number;
    convertidas: number;
    conversionRate: number;
    vigentes: number;
    porVencer: number;
    montoVigente: number;
    expiringSoonDays: number;
    showConversionKpi: boolean;
}

/**
 * Condiciones por defecto del PDF (mismos textos que los defaults del API). El de USD anterior se
 * conserva para reconocerlo: quedó persistido en los tenants que nunca editaron la configuración y,
 * hasta que el API lo migre al leer, el PDF lo reemplaza por el nuevo (QA 2026-10).
 */
export const DEFAULT_PDF_TERMS = 'Precios sujetos a la vigencia indicada en este documento. ' + 'Los montos se expresan en dólares y se facturan a la tasa BCV del día del pago.';
export const LEGACY_PDF_TERMS =
    'Precios sujetos a la vigencia indicada en este documento. No incluye instalación salvo que se especifique. ' + 'Los montos se expresan en dólares y se facturan a la tasa BCV del día del pago.';
export const DEFAULT_PDF_TERMS_VES =
    'Precios sujetos a la vigencia indicada en este documento. ' + 'Los montos se expresan en bolívares indexados a la tasa BCV del día del pago (tasa del día: Bs {tasa} por USD).';

/** Configuración del módulo, por negocio. */
export interface PriceTemplateSettings {
    _id?: string;
    tenantId?: string;
    expiryDays: number;
    prefix: string;
    /** Condiciones al pie del PDF de una cotización en USD. */
    pdfTerms: string;
    /** Condiciones al pie del PDF de una cotización en Bs; `{tasa}` se sustituye por la tasa de la cotización. */
    pdfTermsVes?: string;
    defaultNote: string;
    autoPrefill: boolean;
    warnDuplicates: boolean;
    showConversionKpi: boolean;
    /** FINA-3272: la fecha de vencimiento es obligatoria. Apagado, la cotización puede no vencer. */
    requireExpiry: boolean;
    /** FINA-3277: el cliente es obligatorio. Apagado, se puede cotizar sin cliente. */
    requireCustomer: boolean;
    /** Los exportables (PDF y Excel) muestran el IVA y lo suman al total. */
    includeIva?: boolean;
    /** Los exportables de cotizaciones en USD muestran el IGTF 3% (pago en divisas) y lo suman al total. */
    includeIgtf?: boolean;
}
